import PropTypes from "prop-types"
import React, { useCallback, useEffect, useRef, useState } from "react"
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form"
import { useMarina } from "src/contexts/MarinaContext"

import Button from "src/components/Button"
import ContactBoatPicker from "src/components/ContactBoatPicker"
import useContactBoatPicker, {
  PANEL_ARCHIVED,
  PANEL_NONE,
} from "src/components/ContactBoatPicker/useContactBoatPicker"
import DiscardConfirmModal from "src/components/DiscardConfirmModal"
import Form from "src/components/Form"
import Modal from "src/components/Modal"

import { createWorkOrder } from "src/api/Service/WorkOrders"

import { useDiscardConfirm } from "src/hooks/use_discard_confirm"
import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import { CREATED, CREATE_DISCARDED } from "./amplitude_events"

const BOAT_DEFAULTS = {
  name: "",
  boat_type: "",
  length_overall: 0,
  beam: 0,
  draw: 0,
  height: 0,
  make: "",
  model: "",
  year: "",
  hailing_port: "",
  hull_identification: "",
  registration_attributes: { number: "" },
}

const CONTACT_DEFAULTS = { name: "", email: "", phone: "" }

const DEFAULT_VALUES = {
  contact_id: "",
  contact_boat_id: "",
  request_description: "",
  contact: CONTACT_DEFAULTS,
  contact_boat: BOAT_DEFAULTS,
}

const CreateWorkOrderModal = ({ isOpen, onClose }) => {
  const methods = useForm({
    defaultValues: DEFAULT_VALUES,
    shouldUnregister: true,
  })

  return (
    <FormProvider {...methods}>
      <CreateWorkOrderModalContent isOpen={isOpen} onClose={onClose} />
    </FormProvider>
  )
}

const CreateWorkOrderModalContent = ({ isOpen, onClose }) => {
  const { marinaSlug } = useMarina()
  const showToast = useToast()
  const tracker = useTracker()
  const redirectTimeoutRef = useRef(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const picker = useContactBoatPicker()

  const {
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors },
  } = useFormContext()

  const contactId = watch("contact_id")
  const contactBoatId = watch("contact_boat_id")
  const requestDescription = watch("request_description")
  const contactFields = watch("contact")
  const contactBoatFields = watch("contact_boat")

  const hasUnsavedWork =
    Boolean(contactId) ||
    Boolean(contactBoatId) ||
    Boolean(requestDescription?.trim()) ||
    picker.panel !== PANEL_NONE ||
    Object.values(contactFields || {}).some((v) => v && String(v).trim()) ||
    Object.values(contactBoatFields || {}).some(
      (v) =>
        v && (typeof v === "object" ? Object.values(v).some(Boolean) : v !== 0)
    )

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) clearTimeout(redirectTimeoutRef.current)
    }
  }, [])

  const closeEverything = useCallback(() => {
    picker.cancelPanel()
    onClose()
  }, [picker, onClose])

  const { handleCloseRequest, discardConfirmProps } = useDiscardConfirm({
    isDirty: hasUnsavedWork,
    onClose: closeEverything,
  })

  const handleDiscard = useCallback(() => {
    tracker.trackEvent(CREATE_DISCARDED)
    discardConfirmProps.onDiscard()
  }, [tracker, discardConfirmProps])

  const onSubmit = async (values) => {
    setIsSubmitting(true)
    try {
      const { contactId, contactBoatId: resolvedBoatId } =
        await picker.resolveSelection(values)
      const data = await createWorkOrder(marinaSlug, {
        contactId,
        contactBoatId: resolvedBoatId,
        requestDescription: values.request_description,
      })
      tracker.trackEvent(CREATED, {})
      closeEverything()
      showToast("Work order created. Redirecting...", {
        type: "success",
        duration: 2,
      })
      redirectTimeoutRef.current = setTimeout(() => {
        window.location.href = data.path
      }, 300)
    } catch (error) {
      // Archived pivot is handled inside resolveSelection / ContactBoatPicker.
      if (error?.archivedContact) return
      const message = Array.isArray(error?.message)
        ? error.message.join(". ")
        : error?.message
      showToast(message || error?.error || "Failed to create work order.", {
        type: "error",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const submitDisabled =
    isSubmitting ||
    picker.isWorking ||
    picker.panel === PANEL_ARCHIVED ||
    (picker.panel === PANEL_NONE && !contactBoatId)

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleCloseRequest}
        afterLeave={reset}
        size="default"
      >
        <Modal.Header title="Create Work Order" />
        <Form
          onSubmit={handleSubmit(onSubmit)}
          className="flex min-h-0 flex-1 flex-col"
        >
          <Modal.Body>
            <div className="flex flex-col gap-4">
              <ContactBoatPicker handle={picker} />
              <div>
                <Form.Label htmlFor="work-order-description">
                  Request Description
                </Form.Label>
                <Controller
                  name="request_description"
                  control={control}
                  render={({ field }) => (
                    <Form.Textarea
                      id="work-order-description"
                      rows={4}
                      placeholder="What did the customer request?"
                      {...field}
                    />
                  )}
                />
                {errors.request_description && (
                  <Form.Error>{errors.request_description.message}</Form.Error>
                )}
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                type="button"
                onClick={handleCloseRequest}
                disabled={isSubmitting || picker.isWorking}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                isLoading={isSubmitting || picker.isWorking}
                disabled={submitDisabled}
              >
                Create Work Order
              </Button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      <DiscardConfirmModal {...discardConfirmProps} onDiscard={handleDiscard} />
    </>
  )
}

CreateWorkOrderModalContent.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

CreateWorkOrderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default CreateWorkOrderModal
